import * as React from 'react'
import { observer } from 'mobx-react'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '../../../../../components/RadioButtonGroup'
import { ActionToggle } from 'components/ActionToggle'
import {
  FormColumn,
  FormColumns,
  FormGroupContainer,
  FormRow,
  NumericInputContainer,
} from '../../../../../components/inputs'
import { ExtensionBillingViewModel } from './ExtensionBillingViewModel'
import {
  ProgressionEditorViewModel,
  RegularEditorViewModel,
} from './BillingEditorViewModel'
import {
  InventoryItemExtensionBillingType,
  InventoryItemExtensionType,
} from '../../../InventoryItem'
import styles from '../InventoryItemModalForm.sass'
import cx from 'classnames'
import { IconButton } from '../../../../../components/IconButton'
import Icon from '../../../../../components/Icon'

interface Props {
  billingVM: ExtensionBillingViewModel
  editing?: boolean
  overridden?: boolean
}

export default observer(({ editing, billingVM, overridden }: Props) => (
  <>
    <FormRow>
      <FormGroupContainer label="Extension type">
        <RadioButtonGroup size="big-wide">
          <RadioButtonItem
            onClick={() =>
              billingVM.setExtensionType(InventoryItemExtensionType.Personal)
            }
            active={
              billingVM.extensionType === InventoryItemExtensionType.Personal
            }
            disabled={editing || overridden}
            dataTest="inventory-item-extension-type-personal-button"
          >
            Personal
          </RadioButtonItem>
          <RadioButtonItem
            onClick={() =>
              billingVM.setExtensionType(InventoryItemExtensionType.Business)
            }
            active={
              billingVM.extensionType === InventoryItemExtensionType.Business
            }
            disabled={editing || overridden}
            dataTest="inventory-item-extension-type-business-button"
          >
            Business
          </RadioButtonItem>
          <RadioButtonItem
            onClick={() =>
              billingVM.setExtensionType(InventoryItemExtensionType.Outsourcing)
            }
            active={
              billingVM.extensionType === InventoryItemExtensionType.Outsourcing
            }
            disabled={editing || overridden}
            dataTest="inventory-item-extension-type-outsourcing-button"
          >
            Outsourcing
          </RadioButtonItem>
        </RadioButtonGroup>
      </FormGroupContainer>
    </FormRow>

    <FormRow>
      <FormGroupContainer label="Billing type">
        <RadioButtonGroup size="big-wide">
          <RadioButtonItem
            onClick={() =>
              billingVM.setExtensionBillingType(
                InventoryItemExtensionBillingType.FlatFee,
              )
            }
            active={
              billingVM.billingType ===
              InventoryItemExtensionBillingType.FlatFee
            }
            disabled={editing || overridden}
            dataTest="inventory-item-extension-billing-type-flat-fee-button"
          >
            Flat fee
          </RadioButtonItem>
          <RadioButtonItem
            onClick={() =>
              billingVM.setExtensionBillingType(
                InventoryItemExtensionBillingType.Percentage,
              )
            }
            active={
              billingVM.billingType ===
              InventoryItemExtensionBillingType.Percentage
            }
            disabled={editing || overridden}
            dataTest="inventory-item-extension-billing-type-percentage-button"
          >
            Percentage
          </RadioButtonItem>
        </RadioButtonGroup>
      </FormGroupContainer>
    </FormRow>
    <FormRow>
      <ActionToggle
        label="Job Progression Dependant"
        description=""
        value={billingVM.enableExtensionJobProgressionBillingInfo}
        onChange={() =>
          billingVM.setExtensionJobProgressionBillingInfo(
            !billingVM.enableExtensionJobProgressionBillingInfo,
          )
        }
        dataTest="inventory-item-modal-extension-job-progression-billing-info"
      />
    </FormRow>
    {billingVM.enableExtensionJobProgressionBillingInfo ? (
      <JobProgressionBilling vm={billingVM.progressionBillingVM} />
    ) : (
      <ExtensionPriceInputs vm={billingVM.regularBillingVM} />
    )}
  </>
))

export const ExtensionPriceInputs = observer(
  ({ vm }: { vm: RegularEditorViewModel }) => (
    <FormRow>
      <FormColumns>
        <FormColumn>
          <NumericInputContainer
            precision={vm.isFlat ? 2 : 0}
            label={`Client ${vm.isFlat ? 'amount ($)' : 'percentage (%)'}`}
            field="clientAmount"
            model={vm}
            error={vm.validation.getError('clientAmount')}
            data-test="inventory-item-modal-price-input"
            help={` ${vm.isFlat ? 'The amount to charge the client' : 'The percentage to charge the client based on the remaining total amount on the job'}`}
          />
        </FormColumn>

        <FormColumn>
          <NumericInputContainer
            precision={vm.isFlat ? 2 : 0}
            label={`Pro payout ${vm.isFlat ? 'amount ($)' : 'percentage (%)'}`}
            model={vm}
            field="proPayoutAmount"
            error={vm.validation.getError('proPayoutAmount')}
            data-test="inventory-item-modal-pro-price-input"
            help={` ${vm.isFlat ? 'The amount to pay the pro' : 'The percentage to pay the pro based on the remaining pro payout amount on the job'}`}
          />
        </FormColumn>
      </FormColumns>
    </FormRow>
  ),
)

const JobProgressionBilling = observer(
  ({ vm }: { vm: ProgressionEditorViewModel }) => (
    <FormRow>
      <FormGroupContainer>
        <label className={styles.supportingItemsLabel}>
          <div>
            Job Progression
            <span className={styles.supportingItemCount}>
              ({vm.selectedProgressions.size || 0})
            </span>
          </div>

          {vm.canAddProgression && (
            <IconButton
              className={styles.extensionJobProgressionRowManage}
              onClick={() => vm.modalVM.show()}
            >
              <Icon name="add" size={24} />
            </IconButton>
          )}
        </label>
        {vm.selectedProgressions.size === 0 && (
          <div
            className={cx(styles.extensionJobProgression, styles.noneAssigned)}
          >
            None assigned
          </div>
        )}

        {vm.selectedProgressionsList.map((progression) => (
          <div
            key={progression.value}
            className={styles.extensionJobProgressionContainer}
          >
            <div className={styles.extensionJobProgressionText}>
              <div className={styles.extensionJobProgressionTitle}>
                {progression.name}
              </div>

              <div className={styles.extensionJobProgressionSteps}>
                {progression.steps.map(
                  (step, index) =>
                    `${step.name} ${index === progression.steps.length - 1 ? '' : '| '}`,
                )}
              </div>
            </div>

            <div className={styles.extensionJobProgressionActions}>
              <IconButton onClick={() => vm.editProgression(progression)}>
                <Icon name="edit-outlined" size={24} />
              </IconButton>

              <IconButton
                onClick={() => vm.removeProgression(progression.value)}
              >
                <Icon name="close" size={24} />
              </IconButton>
            </div>
          </div>
        ))}
      </FormGroupContainer>
    </FormRow>
  ),
)
